// src/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase"; // Adjust path based on your project
import { canAccessAdminType, normalizeAdminType, resolveAdminRecord } from "./utils/adminAccess";

const ProtectedRoute = ({ children, allowedAdminTypes = ['admin', 'superAdmin'], fallbackPath = '/dashboard' }) => {
  const [loading, setLoading] = useState(true);
  const [accessState, setAccessState] = useState({
    isAuthenticated: false,
    isVerified: false,
    isRoleAllowed: false,
    adminType: ''
  });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAccessState({
          isAuthenticated: false,
          isVerified: false,
          isRoleAllowed: false,
          adminType: ''
        });
        setLoading(false);
        return;
      }

      try {
        const adminData = await resolveAdminRecord(user);

        if (!adminData || String(adminData.accessStatus || 'active').toLowerCase() === 'disabled') {
          setAccessState({
            isAuthenticated: false,
            isVerified: false,
            isRoleAllowed: false,
            adminType: ''
          });
          setLoading(false);
          return;
        }

        const phone = adminData.phone || adminData.phoneNumber;
        if (!phone) {
          setAccessState({
            isAuthenticated: false,
            isVerified: false,
            isRoleAllowed: false,
            adminType: ''
          });
          setLoading(false);
          return;
        }

        const otpDoc = await getDoc(doc(db, "otps", phone));
        const isVerified = otpDoc.exists() && otpDoc.data().verified === true;
        const adminType = normalizeAdminType(adminData.adminType);
        const isRoleAllowed = canAccessAdminType(adminType, allowedAdminTypes);

        setAccessState({
          isAuthenticated: true,
          isVerified,
          isRoleAllowed,
          adminType
        });
      } catch (err) {
        console.error("Access check failed", err);
        setAccessState({
          isAuthenticated: false,
          isVerified: false,
          isRoleAllowed: false,
          adminType: ''
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [allowedAdminTypes]);

  if (loading) return <div>Loading...</div>;

  if (!accessState.isAuthenticated || !accessState.isVerified) {
    return <Navigate to="/login" replace />;
  }

  if (!accessState.isRoleAllowed) {
    const redirectPath = accessState.adminType === 'admin' || accessState.adminType === 'superAdmin'
      ? fallbackPath
      : '/login';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
